/* eslint-disable ember/classic-decorator-hooks, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-observers, ember/no-runloop, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { isArray } from '@ember/array';
import { service } from '@ember/service';
import { action, computed } from '@ember/object';
import { later, next } from '@ember/runloop';
import { isEmpty } from '@ember/utils';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import { tagName } from '@ember-decorators/component';
import { observes } from '@ember-decorators/object';
import { byKeys } from '@linc-technologies/linced-common/utils/arrays';

@tagName('')
export default class FilteredPeopleList extends Component {
  onCardClose() {}

  // Services
  @service
  permissions;

  @service
  session;

  @service
  store;

  setDefaultFilterType() {
    let filterType = 'first';
    if (this.permissions.meet('urn:linced:organisation:role:admin')) {
      filterType = 'last';
    }
    this.filterType = filterType;
  }

  filterTypes = Object.freeze([
    { value: 'first', caption: 'First name' },
    { value: 'last', caption: 'Last name' },
  ]);

  filterValues = Object.freeze([
    { value: 'A' },
    { value: 'B' },
    { value: 'C' },
    { value: 'D' },
    { value: 'E' },
    { value: 'F' },
    { value: 'G' },
    { value: 'H' },
    { value: 'I' },
    { value: 'J' },
    { value: 'K' },
    { value: 'L' },
    { value: 'M' },
    { value: 'N' },
    { value: 'O' },
    { value: 'P' },
    { value: 'Q' },
    { value: 'R' },
    { value: 'S' },
    { value: 'T' },
    { value: 'U' },
    { value: 'V' },
    { value: 'W' },
    { value: 'X' },
    { value: 'Y' },
    { value: 'Z' },
  ]);

  @tracked filterType = 'first';
  @tracked filterValue = '';
  open = false;

  @observes('open')
  resetOnClose() {
    if (!this.open) {
      // When closed, wait a second before resetting the filter so that the selector has had time to visibly close.
      later(
        this,
        function () {
          this.filterType = 'first';
          this.filterValue = '';
        },
        1000,
      );
    }
  }

  @tracked
  filteringInProgress = false;

  @computed('people.[]', 'filterType', 'filterValue', 'default.[]')
  get filteredPeople() {
    let initial = this.filterType + 'InitialBase';
    let value = this.filterValue;
    if (isEmpty(value)) {
      this.doneFiltering();
      return isArray(this.default) ? this.default : [];
    }
    let sortBy = `${this.filterType}Name`;

    let results = this.people.filter((person) => {
      // For Learners, check their initial and whether they're not-former.
      if (
        person.type === PersonType.LEARNER &&
        person.get(initial).toUpperCase() === value
      ) {
        let status = person.get('learner.enrolmentStub.status');
        return (
          status !== EnrolmentStatus.FORMER &&
          status !== EnrolmentStatus.ARCHIVED
        );
      }

      // For staff, check if they're current.
      if (person.type === PersonType.STAFF) {
        if (isEmpty(person.userId)) {
          return false;
        } else {
          let user = this.store.peekRecord('user', person.userId);
          if (isEmpty(user) || user.disabled) {
            return false;
          }
        }

        // Staff member is current (or has no user record so can't be checked).  Check their initial.
        return person.get(initial).toUpperCase() === value;
      }

      // Not a learner or current staff member with the correct initial.
      return false;
    });

    this.doneFiltering();
    return results.sort(byKeys(sortBy));
  }

  doneFiltering() {
    next(this, () => (this.filteringInProgress = false));
  }

  init() {
    super.init(...arguments);
    this.setDefaultFilterType();
  }

  @action
  changeFilterType(newFilter) {
    if (newFilter === this.filterType) {
      // No change - don't try filtering again.
      return;
    }
    this.filteringInProgress = true;
    next(this, () => (this.filterType = newFilter));
  }

  @action
  changeFilterValue(newValue) {
    if (newValue === this.filterValue) {
      // No change - don't try filtering again.
      return;
    }
    this.filteringInProgress = true;
    next(this, () => (this.filterValue = newValue));
  }
}
